import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import { usePlaybackCoordinator } from '@/contexts/PlaybackCoordinatorContext';
// @ts-ignore — JS profiling util
import { useRenderProfiler } from '@/utils/renderProfiler';

export interface VideoPlayerProps {
    url: string;
    type?: string;
    poster?: string;
    renderControls?: (data: { state: any }) => React.ReactNode; // Aggiunta renderProps
    /**
     * Opt-in: when set, this player participates in the shared
     * PlaybackCoordinator so that starting playback here pauses any other
     * card (smooth-jump or video.js) currently holding the active slot,
     * and vice versa. Leave undefined to keep the player fully standalone.
     */
    coordinatorId?: string;
    /**
     * Opt-in: video.js `preload` strategy. Leave undefined to use the
     * video.js default ('auto'). Set to 'none' on the events page so that
     * idle live cards don't eagerly load their HLS manifest and start the
     * VHS live-refresh polling loop before the user presses play.
     */
    preload?: 'auto' | 'metadata' | 'none';
}

export interface VideoPlayerRef {
    play: () => void;
    pause: () => void;
    toggleFullscreen: () => void;
    setMuted: (m: boolean) => void;
    setVolume: (v01: number) => void;
    getPlayer: () => any;
    seekToLive: () => void;
}

const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>(
    ({ url, type = 'video/mp4', poster, renderControls, coordinatorId, preload }, ref) => {
        useRenderProfiler('VideoPlayer', { url, type, poster, renderControls, coordinatorId, preload });

        const videoNodeRef = useRef<HTMLVideoElement | null>(null);
        const playerRef = useRef<any>(null);
        const containerRef = useRef<HTMLDivElement | null>(null); // Ref per il fullscreen
        const coordinator = usePlaybackCoordinator();

        // Stato minimo per le icone
        const [uiState, setUiState] = useState({
            isPlaying: false,
            isFullscreen: false,
        });

        const safePlay = (player: any) => {
            const result = player.play();
            if (result && typeof result.catch === 'function') {
                result.catch((e: any) => console.warn('Autoplay bloccato:', e));
            }
        };

        useEffect(() => {
            // Fullscreen listener — always needed regardless of url.
            const handleFs = () => {
                setUiState(s => ({ ...s, isFullscreen: !!document.fullscreenElement }));
            };
            document.addEventListener('fullscreenchange', handleFs);

            return () => {
                document.removeEventListener('fullscreenchange', handleFs);
                if (playerRef.current) {
                    playerRef.current.dispose();
                    playerRef.current = null;
                }
            };
        }, []);

        useEffect(() => {
            if (!url || !videoNodeRef.current) return;
            if (!playerRef.current) {
                // Init VideoJS only once we have a valid URL — avoids MEDIA_ERR_SRC_NOT_SUPPORTED
                // when the component mounts before the URL is available.
                playerRef.current = videojs(videoNodeRef.current, {
                    controls: false,
                    autoplay: false,
                    responsive: true,
                    fill: true,
                    sources: [{ src: url, type }],
                    muted: false,
                    ...(preload && { preload }),
                    ...(poster && { poster }),
                });
                playerRef.current.on('play', () => {
                    setUiState(s => ({ ...s, isPlaying: true }));
                    if (coordinatorId) coordinator.claimActive(coordinatorId);
                });
                playerRef.current.on('pause', () => {
                    setUiState(s => ({ ...s, isPlaying: false }));
                    if (coordinatorId) coordinator.releaseActive(coordinatorId);
                });
            } else {
                playerRef.current.src({ src: url, type });
            }
        }, [url, type]);

        useEffect(() => {
            if (!coordinatorId) return;
            coordinator.registerPausable(coordinatorId, () => {
                if (playerRef.current && !playerRef.current.paused()) {
                    playerRef.current.pause();
                }
            });
            return () => {
                coordinator.unregisterPausable(coordinatorId);
            };
        }, [coordinatorId, coordinator]);

        useImperativeHandle(ref, () => ({
            play: () => {
                if (playerRef.current) safePlay(playerRef.current);
            },
            pause: () => {
                if (playerRef.current) playerRef.current.pause();
            },
            toggleFullscreen: () => {
                if (!containerRef.current) return;
                if (!document.fullscreenElement) {
                    containerRef.current.requestFullscreen?.();
                } else {
                    document.exitFullscreen?.();
                }
            },
            setMuted: (m: boolean) => {
                if (playerRef.current) playerRef.current.muted(!!m);
            },
            setVolume: (v01: number) => {
                if (playerRef.current) {
                    const clamped = Math.max(0, Math.min(1, Number(v01) || 0));
                    playerRef.current.volume(clamped);
                }
            },
            getPlayer: () => playerRef.current,

            seekToLive: () => {
                const p = playerRef.current;
                if (!p) return;
                try {
                    if (p.liveTracker && p.liveTracker.isLive()) {
                        p.liveTracker.seekToLiveEdge();
                    } else if (typeof p.currentTime === 'function' && typeof p.duration === 'function') {
                        const duration = p.duration();
                        if (duration && duration !== Infinity) {
                            p.currentTime(duration);
                        }
                    }
                } catch (error) {
                    console.warn("Impossible to jump al live edge", error);
                }
            }
        }));

        return (
            <div ref={containerRef} style={{ position: 'relative', width: '100%', height: '100%', background: 'black' }}>
                <div data-vjs-player style={{ width: '100%', height: '100%' }}>
                    <video
                        disablePictureInPicture
                        ref={videoNodeRef}
                        className="video-js vjs-big-play-centered"
                        style={{ width: '100%', height: '100%'}}
                        playsInline
                    />
                </div>

                {/* Overlay per le tue icone */}
                {renderControls && (
                    <div style={{ position: 'absolute', inset: 0, zIndex: 10, pointerEvents: 'none' }}>
                        {renderControls({ state: uiState })}
                    </div>
                )}
            </div>
        );
    }
);

VideoPlayer.displayName = 'VideoPlayer';
export default VideoPlayer;